import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getSimplifiedInputEventTypes, getStartEventTypes } from '@bliks-io/ui';
import { useUpdateDraftEvent } from '@/api/mutations/useProcessDraft';
import type { DraftEditorEvent } from '../draftEvents';
import { EventDetailPanel, type EventDetailPanelPatch } from './EventDetailPanel';

interface Props {
  processId: string;
  event: DraftEditorEvent;
  onClose: () => void;
}

/**
 * The ONE draft event panel (ADR 0003 P3b): the start event is a regular
 * event node, distinguished only by its `eventKind` — it gets the start-event
 * type choices and copy, and its edits persist through the same draft event
 * endpoint (whose start arm writes through to the live start node).
 */
export function DraftEventPanel({ processId, event, onClose }: Props) {
  const { t } = useTranslation();
  const updateEvent = useUpdateDraftEvent(processId);
  const isStart = event.eventKind === 'start';

  const handleCommit = useCallback(
    (patch: EventDetailPanelPatch) => {
      void updateEvent.mutateAsync({ eventId: event._id, patch });
    },
    [event._id, updateEvent]
  );

  return (
    <EventDetailPanel
      nodeId={event._id}
      i18nPrefix={isStart ? 'processDraft.startEvent' : 'processDraft.event'}
      fieldIdPrefix={isStart ? 'start-event' : 'event'}
      eventTypes={isStart ? getStartEventTypes() : getSimplifiedInputEventTypes()}
      value={event}
      onCommit={handleCommit}
      onClose={onClose}
      titleHint={isStart ? t('processDraft.startEvent.titleHint') : undefined}
      eventTypeHint={isStart ? t('processDraft.startEvent.eventTypeHint') : undefined}
      descriptionRequired={isStart}
    />
  );
}
